import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';

interface DropdownItem {
  to: string;
  label: string;
}

interface NavDropdownProps {
  icon: React.ReactNode;
  label: string;
  items: DropdownItem[];
  onItemClick?: () => void;
}

const ChevronDown = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="dropdown-chevron"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const NavDropdown: React.FC<NavDropdownProps> = ({ icon, label, items, onItemClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const location = useLocation();

  // Check if any dropdown item is active
  const isAnyItemActive = items.some(item => location.pathname.startsWith(item.to));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
  }, [location.pathname]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          event.preventDefault();
          setIsOpen(true);
          setFocusedIndex(0);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(prev => (prev + 1) % items.length);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => (prev - 1 + items.length) % items.length);
        }
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          setFocusedIndex(-1);
        }
        break;
    }
  }, [isOpen, items.length]);

  // Focus menu item when focusedIndex changes
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuRef.current) {
      const menuItems = menuRef.current.querySelectorAll<HTMLAnchorElement>('.dropdown-item');
      menuItems[focusedIndex]?.focus();
    }
  }, [focusedIndex, isOpen]);

  const handleMouseEnter = () => {
    // Only hover-open on desktop
    if (window.innerWidth > 768) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    // Only hover-close on desktop
    if (window.innerWidth > 768) {
      setIsOpen(false);
      setFocusedIndex(-1);
    }
  };

  const handleTriggerClick = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setFocusedIndex(0);
    }
  };

  const handleItemClick = () => {
    setIsOpen(false);
    setFocusedIndex(-1);
    onItemClick?.();
  };

  return (
    <li
      className={`nav-item nav-dropdown ${isOpen ? 'open' : ''}`}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={triggerRef}
        className={`nav-link dropdown-trigger ${isAnyItemActive ? 'active' : ''}`}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        type="button"
      >
        {icon}
        {label}
        <ChevronDown />
      </button>

      <ul
        ref={menuRef}
        className="dropdown-menu"
        role="menu"
        aria-label={`${label} submenu`}
      >
        {items.map((item, index) => (
          <li key={item.to} role="none">
            <RouterNavLink
              to={item.to}
              className={({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`}
              onClick={handleItemClick}
              onKeyDown={handleKeyDown}
              role="menuitem"
              tabIndex={isOpen ? 0 : -1}
            >
              {item.label}
            </RouterNavLink>
          </li>
        ))}
      </ul>
    </li>
  );
};

export default NavDropdown;
